"use client"

import { useState } from "react"
import { Container, Row, Col, Card, Alert } from "react-bootstrap"
import MapComponent from "../components/MapComponent"
import WeatherChart from "../components/WeatherChart"
import CropTable from "../components/CropTable"
import PlantingCalendar from "../components/PlantingCalendar"
import LocationSummary from "../components/LocationSummary"
import YearRangeSelector from "../components/YearRangeSelector"
import SuitabilityFilter from "../components/SuitabilityFilter"
import HourlySuitabilityReport from "../components/HourlySuitabilityReport"
import { toast } from "react-hot-toast"
import { apiRequest, endpoints } from "../utils/api"

const HomePage = () => {
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [yearRange, setYearRange] = useState(5)
  const [loading, setLoading] = useState(false)
  const [cropAnalysisLoading, setCropAnalysisLoading] = useState(false)
  const [weatherLoading, setWeatherLoading] = useState(false)
  const [error, setError] = useState(null)
  const [suitabilityFilter, setSuitabilityFilter] = useState(0)
  const [currentMonthPreference] = useState(true)
  const [selectedCrop, setSelectedCrop] = useState(null)
  const [locationSelectionLocked, setLocationSelectionLocked] = useState(false)

  const handleLocationSelect = async (lat, lng) => {
    if (locationSelectionLocked) {
      toast.warning("Please wait for the current analysis to complete before selecting a new location")
      return
    }

    setLocationSelectionLocked(true)
    setLoading(true)
    setCropAnalysisLoading(true)
    setWeatherLoading(true)
    setError(null)

    try {
      const weatherResponse = await apiRequest(`${endpoints.weather}?lat=${lat}&lng=${lng}&years=${yearRange}`)
      setWeatherLoading(false)

      const cropResponse = await apiRequest(
        `${endpoints.suitableCrops}?lat=${lat}&lng=${lng}&years=${yearRange}&currentMonth=${currentMonthPreference}`,
      )
      setCropAnalysisLoading(false)

      setSelectedLocation({
        lat,
        lng,
        name: `Location ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
        weatherData: weatherResponse.data,
        ...cropResponse,
      })

      const seasonalMessage = currentMonthPreference
        ? ` (${cropResponse.crops.filter((c) => c.isCurrentSeason).length} suitable for current season)`
        : ""

      toast.success(`Found ${cropResponse.suitableCrops} suitable crops for this location!${seasonalMessage}`)
    } catch (error) {
      console.error("Error fetching location data:", error)
      setError(error.message)
      toast.error("Failed to analyze location. Please try again.")
      setCropAnalysisLoading(false)
      setWeatherLoading(false)
    } finally {
      setLoading(false)
      setLocationSelectionLocked(false)
    }
  }

  const handleYearRangeChange = (newYearRange) => {
    setYearRange(newYearRange)

    if (selectedLocation) {
      handleLocationSelect(selectedLocation.lat, selectedLocation.lng)
    }
  }

  const getFilteredCrops = () => {
    if (!selectedLocation?.crops) return []

    return selectedLocation.crops.filter((crop) => {
      const suitability = crop.suitabilityScore || 85
      return suitability >= suitabilityFilter
    })
  }

  const filteredCrops = getFilteredCrops()
  const currentSeasonCrops = filteredCrops.filter((crop) => crop.isCurrentSeason)

  const handleCropSelect = (crop) => {
    setSelectedCrop(crop)
    toast.success(`Selected ${crop.name} for detailed hourly analysis`)
  }

  return (
    <div className="bg-light min-vh-100">
      <Container fluid className="py-4">
        {/* Hero Section */}
        <Row className="mb-4">
          <Col>
            <Card className="bg-success text-white">
              <Card.Body className="text-center py-5">
                <h1 className="display-4 fw-bold mb-3">🌱 Smart Crop Suitability Advisor</h1>
                <p className="lead mb-0">
                  Click anywhere on the map to get personalized crop recommendations and optimal planting dates based on
                  historical weather data
                </p>
                {locationSelectionLocked && (
                  <div className="mt-3">
                    <div className="spinner-border spinner-border-sm text-light me-2" role="status">
                      <span className="visually-hidden">Processing...</span>
                    </div>
                    <small>Processing location analysis... Please wait before selecting another location.</small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Map Section */}
          <Col lg={8}>
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">📍 Select Location</h5>
                  <YearRangeSelector value={yearRange} onChange={handleYearRangeChange} />
                </div>
              </Card.Header>
              <Card.Body>
                <MapComponent
                  onLocationSelect={handleLocationSelect}
                  loading={loading}
                  disabled={locationSelectionLocked}
                />
                <div className="mt-3 p-3 bg-light rounded">
                  <h6 className="mb-2">🗺️ Map Legend</h6>
                  <div className="d-flex flex-wrap gap-3 small">
                    <div className="d-flex align-items-center">
                      <div className="bg-primary rounded-circle me-2" style={{ width: "12px", height: "12px" }}></div>
                      <span>Your Current Location</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="bg-success rounded-circle me-2" style={{ width: "12px", height: "12px" }}></div>
                      <span>Selected Analysis Point</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="bg-warning rounded-circle me-2" style={{ width: "12px", height: "12px" }}></div>
                      <span>Search Results</span>
                    </div>
                  </div>
                </div>
                {error && (
                  <Alert variant="danger" className="mt-3">
                    {error}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Results Sidebar */}
          <Col lg={4}>
            {selectedLocation ? (
              <LocationSummary location={selectedLocation} yearRange={yearRange} />
            ) : (
              <Card className="shadow-sm">
                <Card.Body className="text-center py-5">
                  <div className="text-muted">
                    <i className="fas fa-map-marker-alt fa-3x mb-3"></i>
                    <h5>Select a Location</h5>
                    <p>Click anywhere on the map to analyze crop suitability for that location</p>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* Detailed Results */}
        {selectedLocation && (
          <>
            {/* Weather Chart */}
            <Row className="mb-4">
              <Col>
                <Card className="shadow-sm">
                  <Card.Header className="bg-white">
                    <h5 className="mb-0">🌡️ Weather Analysis</h5>
                  </Card.Header>
                  <Card.Body>
                    {weatherLoading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading weather data...</span>
                        </div>
                        <p className="mt-3 text-muted">Analyzing weather patterns...</p>
                      </div>
                    ) : (
                      <WeatherChart weatherData={selectedLocation.weatherData} yearRange={yearRange} />
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {cropAnalysisLoading ? (
              <Row className="mb-4">
                <Col>
                  <Card className="shadow-sm">
                    <Card.Body className="text-center py-5">
                      <div className="spinner-border text-success" role="status">
                        <span className="visually-hidden">Analyzing crop suitability...</span>
                      </div>
                      <p className="mt-3 text-muted">Calculating crop recommendations...</p>
                      <small className="text-muted">This may take a few moments</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            ) : (
              currentSeasonCrops.length > 0 && (
                <Row className="mb-4">
                  <Col>
                    <Card className="shadow-sm border-success">
                      <Card.Header className="bg-success text-white">
                        <h5 className="mb-0">🌱 Current Season Recommendations ({currentSeasonCrops.length} crops)</h5>
                      </Card.Header>
                      <Card.Body>
                        <p className="text-muted mb-3">These crops are ideal for planting in the current month:</p>
                        <CropTable
                          crops={currentSeasonCrops}
                          showSeasonalBadge={true}
                          onCropSelect={handleCropSelect}
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )
            )}

            {/* Crops Table */}
            <Row className="mb-4">
              <Col>
                <Card className="shadow-sm">
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">🌾 Suitable Crops Analysis</h5>
                    {!cropAnalysisLoading && (
                      <SuitabilityFilter
                        value={suitabilityFilter}
                        onChange={setSuitabilityFilter}
                        totalCrops={selectedLocation.crops?.length || 0}
                        filteredCrops={filteredCrops.length}
                      />
                    )}
                  </Card.Header>
                  <Card.Body>
                    {cropAnalysisLoading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Loading crop analysis...</span>
                        </div>
                        <p className="mt-3 text-muted">Processing crop suitability data...</p>
                      </div>
                    ) : (
                      <CropTable crops={filteredCrops} onCropSelect={handleCropSelect} selectedCrop={selectedCrop} />
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Hourly Suitability Report */}
            {selectedCrop && (
              <Row className="mb-4">
                <Col>
                  <HourlySuitabilityReport location={selectedLocation} selectedCrop={selectedCrop} />
                </Col>
              </Row>
            )}

            {/* Planting Calendar */}
            <Row className="mb-4">
              <Col>
                <Card className="shadow-sm">
                  <Card.Header className="bg-white">
                    <h5 className="mb-0">📅 Planting Calendar</h5>
                  </Card.Header>
                  <Card.Body>
                    <PlantingCalendar crops={filteredCrops} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  )
}

export default HomePage
